import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMoney, parseAmountToCents } from "@/lib/money";
import { Copy, Loader2, Share2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface Profile {
  cashtag: string;
  store_name: string | null;
  display_name: string;
}

const MerchantCharge = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [received, setReceived] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("cashtag, store_name, display_name")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile(data as Profile | null));
  }, [user]);

  const cents = parseAmountToCents(amount) ?? 0;

  const payUrl = useMemo(() => {
    if (!profile?.cashtag) return "";
    const qs = new URLSearchParams();
    if (cents > 0) qs.set("amount", (cents / 100).toFixed(2));
    if (note) qs.set("note", note);
    const q = qs.toString();
    return `${window.location.origin}/pay/${profile.cashtag}${q ? `?${q}` : ""}`;
  }, [profile, cents, note]);

  // Listen for an incoming payment matching this charge
  useEffect(() => {
    if (!user || !waiting || cents === 0) return;
    const channel = supabase
      .channel(`charge-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "transactions",
          filter: `receiver_id=eq.${user.id}`,
        },
        (payload) => {
          const tx = payload.new as { amount_cents: number };
          if (tx.amount_cents === cents) {
            setReceived(true);
            setWaiting(false);
            toast.success(`Received ${formatMoney(cents)} 🌴`);
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, waiting, cents]);

  const start = () => {
    if (cents === 0) { toast.error("Enter an amount"); return; }
    setReceived(false);
    setWaiting(true);
  };

  const cancel = () => { setWaiting(false); setReceived(false); };

  const copy = async () => {
    await navigator.clipboard.writeText(payUrl);
    toast.success("Link copied");
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: profile?.store_name || "Pay",
          text: `Pay ${formatMoney(cents)}`,
          url: payUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  if (!profile) {
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate(-1)} />
        <div className="flex-1 grid place-items-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </MobileShell>
    );
  }

  if (received) {
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate("/merchant")}>
          <h1 className="text-center mt-5 font-display text-2xl font-bold">Charge</h1>
        </BrandHeader>
        <div className="px-5 py-12 flex-1 flex flex-col items-center text-center animate-fade-in-up">
          <CheckCircle2 className="h-16 w-16 text-success" />
          <h2 className="mt-4 font-display text-2xl font-bold">Payment received</h2>
          <p className="mt-2 text-4xl font-display font-extrabold text-primary">{formatMoney(cents)}</p>
          <div className="mt-8 w-full space-y-2">
            <Button variant="hero" size="xl" className="w-full" onClick={() => { setAmount(""); setNote(""); setReceived(false); }}>
              New charge
            </Button>
            <Button variant="outline" size="xl" className="w-full" onClick={() => navigate("/merchant")}>
              Done
            </Button>
          </div>
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Charge customer</h1>
      </BrandHeader>

      <div className="px-5 py-6 flex-1 space-y-5 animate-fade-in-up">
        {!waiting ? (
          <>
            <div>
              <Label className="text-sm font-medium">Amount</Label>
              <div className="mt-2 flex items-center bg-secondary/60 rounded-xl px-5 h-20">
                <span className="font-display text-4xl font-bold text-muted-foreground mr-1">$</span>
                <input
                  autoFocus
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="bg-transparent flex-1 font-display text-4xl font-bold outline-none w-full"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={80}
                placeholder="e.g. Order #123"
                className="mt-2 h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              />
            </div>

            <Button variant="hero" size="xl" className="w-full" onClick={start} disabled={cents === 0}>
              Generate QR · {cents ? formatMoney(cents) : "—"}
            </Button>
          </>
        ) : (
          <>
            <div className="rounded-2xl bg-card border border-border p-5 text-center">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Show this code</p>
              <p className="font-display text-4xl font-extrabold text-primary mt-1">{formatMoney(cents)}</p>
              {note && <p className="text-xs text-muted-foreground mt-1 truncate">{note}</p>}
              <div className="mt-4 grid place-items-center">
                <div className="bg-white border border-border rounded-xl p-3">
                  <QRCodeSVG value={payUrl} size={220} level="M" fgColor="hsl(184 70% 24%)" />
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Waiting for payment… <Loader2 className="inline h-3 w-3 animate-spin ml-1" />
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={copy}>
                <Copy className="h-4 w-4 mr-1.5" /> Copy link
              </Button>
              <Button variant="outline" onClick={share}>
                <Share2 className="h-4 w-4 mr-1.5" /> Share
              </Button>
            </div>

            <Button variant="ghost" className="w-full" onClick={cancel}>
              Cancel
            </Button>
          </>
        )}
      </div>

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default MerchantCharge;
